import { Parser, Store, DataFactory } from 'n3';
import { ParsedGraph, OntologyClass, OntologyProperty, Triple, OntologyIndividual } from '../types/ontology';

const { namedNode } = DataFactory;

// Common RDF/OWL URIs
const RDF = 'http://www.w3.org/1999/02/22-rdf-syntax-ns#';
const RDFS = 'http://www.w3.org/2000/01/rdf-schema#';
const OWL = 'http://www.w3.org/2002/07/owl#';

const RDF_TYPE = RDF + 'type';
const RDFS_LABEL = RDFS + 'label';
const RDFS_COMMENT = RDFS + 'comment';
const RDFS_SUBCLASS_OF = RDFS + 'subClassOf';
const RDFS_DOMAIN = RDFS + 'domain';
const RDFS_RANGE = RDFS + 'range';
const OWL_CLASS = OWL + 'Class';
const OWL_OBJECT_PROPERTY = OWL + 'ObjectProperty';
const OWL_DATATYPE_PROPERTY = OWL + 'DatatypeProperty';
const OWL_NAMED_INDIVIDUAL = OWL + 'NamedIndividual';

function getLocalName(uri: string): string {
    const hashIndex = uri.lastIndexOf('#');
    const slashIndex = uri.lastIndexOf('/');
    const index = Math.max(hashIndex, slashIndex);
    return index >= 0 ? uri.substring(index + 1) : uri;
}

function getLiteral(store: Store, subject: string, predicate: string, lang = 'en'): string | undefined {
    const quads = store.getQuads(namedNode(subject), namedNode(predicate), null, null);
    // Prefer requested language, fallback to any
    const preferred = quads.find((q: any) => q.object.language === lang);
    const any = quads.find((q: any) => q.object.termType === 'Literal');
    const result = preferred || any;
    return result ? result.object.value : undefined;
}

function getUris(store: Store, subject: string, predicate: string): string[] {
    return store.getQuads(namedNode(subject), namedNode(predicate), null, null)
        .filter((q: any) => q.object.termType === 'NamedNode')
        .map((q: any) => q.object.value);
}

export async function parseRDF(turtle: string): Promise<ParsedGraph> {
    return new Promise((resolve, reject) => {
        const parser = new Parser();
        const store = new Store();
        const prefixes: Record<string, string> = {};

        parser.parse(turtle, (error: any, quad: any, prefixesResult: any) => {
            if (error) {
                reject(error);
                return;
            }

            if (quad) {
                store.addQuad(quad);
            } else {
                // Parsing complete
                if (prefixesResult) {
                    Object.assign(prefixes, prefixesResult);
                }

                try {
                    const graph = buildGraph(store, prefixes);
                    resolve(graph);
                } catch (e) {
                    reject(e);
                }
            }
        });
    });
}

function buildGraph(store: Store, prefixes: Record<string, string>): ParsedGraph {
    const classes: OntologyClass[] = [];
    const objectProperties: OntologyProperty[] = [];
    const datatypeProperties: OntologyProperty[] = [];
    const individuals: OntologyIndividual[] = [];
    const triples: Triple[] = [];

    // Extract all triples
    for (const quad of store.getQuads(null, null, null, null)) {
        triples.push({
            subject: quad.subject.value,
            predicate: quad.predicate.value,
            object: quad.object.value,
            objectType: quad.object.termType === 'NamedNode' ? 'uri' :
                quad.object.termType === 'BlankNode' ? 'blank' : 'literal',
            datatype: quad.object.termType === 'Literal' ? quad.object.datatype?.value : undefined,
            language: quad.object.termType === 'Literal' ? quad.object.language : undefined
        });
    }

    // Extract classes
    const classQuads = store.getQuads(null, namedNode(RDF_TYPE), namedNode(OWL_CLASS), null);
    for (const quad of classQuads) {
        const uri = quad.subject.value;
        if (quad.subject.termType !== 'NamedNode') continue;

        classes.push({
            uri,
            label: getLiteral(store, uri, RDFS_LABEL) || getLocalName(uri),
            comment: getLiteral(store, uri, RDFS_COMMENT),
            subClassOf: getUris(store, uri, RDFS_SUBCLASS_OF)
        });
    }

    // Extract object properties
    const objPropQuads = store.getQuads(null, namedNode(RDF_TYPE), namedNode(OWL_OBJECT_PROPERTY), null);
    for (const quad of objPropQuads) {
        const uri = quad.subject.value;
        if (quad.subject.termType !== 'NamedNode') continue;

        objectProperties.push({
            uri,
            label: getLiteral(store, uri, RDFS_LABEL) || getLocalName(uri),
            type: 'object',
            domain: getUris(store, uri, RDFS_DOMAIN),
            range: getUris(store, uri, RDFS_RANGE),
            comment: getLiteral(store, uri, RDFS_COMMENT)
        });
    }

    // Extract datatype properties
    const dataPropQuads = store.getQuads(null, namedNode(RDF_TYPE), namedNode(OWL_DATATYPE_PROPERTY), null);
    for (const quad of dataPropQuads) {
        const uri = quad.subject.value;
        if (quad.subject.termType !== 'NamedNode') continue;

        datatypeProperties.push({
            uri,
            label: getLiteral(store, uri, RDFS_LABEL) || getLocalName(uri),
            type: 'datatype',
            domain: getUris(store, uri, RDFS_DOMAIN),
            range: getUris(store, uri, RDFS_RANGE),
            comment: getLiteral(store, uri, RDFS_COMMENT)
        });
    }

    // Extract named individuals
    const individualQuads = store.getQuads(null, namedNode(RDF_TYPE), namedNode(OWL_NAMED_INDIVIDUAL), null);
    for (const quad of individualQuads) {
        const uri = quad.subject.value;
        if (quad.subject.termType !== 'NamedNode') continue;

        // Get all types except NamedIndividual
        const types = store.getQuads(namedNode(uri), namedNode(RDF_TYPE), null, null)
            .filter((q: any) => q.object.value !== OWL_NAMED_INDIVIDUAL && q.object.termType === 'NamedNode')
            .map((q: any) => q.object.value);

        individuals.push({
            uri,
            label: getLiteral(store, uri, RDFS_LABEL) || getLocalName(uri),
            types,
            properties: []
        });
    }

    return {
        classes,
        objectProperties,
        datatypeProperties,
        individuals,
        triples,
        prefixes
    };
}
